import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Save, FileDown, Printer, Mail, Plus, Trash2, FileSignature, CheckCircle2, HardDrive, XCircle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { SignaturePad } from '../components/SignaturePad';
import { parseApiError } from '../lib/parseApiError';
import { avvisoDurataMinima } from '../lib/durataMinima';
import { uid } from '../lib/uid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IBAN_DEFAULT = 'IT03A0867654560000000320516';
const BANCA_DEFAULT = 'Banca di Credito Cooperativo Colli Morenici del Garda Soc. Coop. - Filiale di Gavardo (BS)';
const fmtIt = (iso) => (iso ? `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)}/${String(iso).slice(0, 4)}` : '-');

const VUOTO = {
  cliente_id: '', apparecchiature: [], data_inizio: '', data_fine: '', data_inizio_fatturazione: '', canone_annuo: 0,
  durata_minima_anni: '', penale_uscita: '',
  modalita_pagamento: null, riba_banca: '', riba_agenzia: '', riba_iban: '', riba_scadenze: null,
  iban_fornitore: IBAN_DEFAULT, banca_fornitore: BANCA_DEFAULT, indirizzo_impianto: '', luogo_firma: '', note: '', disdetta: false,
  firma_cliente_b64: null, firma_clausole_b64: null, firma_cliente_nome: '',
};

export default function ContrattoPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...VUOTO, cliente_id: params.get('cliente_id') || '' });
  const [clienteNome, setClienteNome] = useState('');
  const [savedId, setSavedId] = useState(id || null);
  const [saving, setSaving] = useState(false);
  const [storicoPagamenti, setStoricoPagamenti] = useState([]);
  const [impiantiCliente, setImpiantiCliente] = useState([]);
  const [pickerImpiantiOpen, setPickerImpiantiOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailExtra, setEmailExtra] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const init = async () => {
      if (id) {
        try {
          const { data } = await axios.get(`${API}/contratti/${id}`);
          setForm({ ...VUOTO, ...data, apparecchiature: (data.apparecchiature || []).map((a) => ({ ...a, _uid: uid() })) });
          setClienteNome(data.cliente_nome || '');
          setStoricoPagamenti(data.storico_pagamenti || []);
          if (data.cliente_id) {
            axios.get(`${API}/impianti?cliente_id=${data.cliente_id}`).then((r) => setImpiantiCliente(r.data || [])).catch(() => {});
          }
        } catch { toast.error('Contratto non trovato'); }
        return;
      }
      const cid = params.get('cliente_id');
      if (!cid) return;
      try {
        const [cliRes, impRes, abbRes, setRes, docRes] = await Promise.all([
          axios.get(`${API}/clienti/${cid}`),
          axios.get(`${API}/impianti?cliente_id=${cid}`),
          axios.get(`${API}/abbonamenti?cliente_id=${cid}`),
          axios.get(`${API}/impostazioni`),
          axios.get(`${API}/documenti?cliente_id=${cid}`),
        ]);
        setClienteNome(cliRes.data.ragione_sociale || '');
        setImpiantiCliente(impRes.data || []);
        const apparecchiature = (impRes.data || []).map((i) => ({
          tipo: i.tipo || '',
          marca_modello: `${i.marca || ''} ${i.modello || ''}`.trim(),
          matricola: i.matricola || '',
          data_installazione: i.data_installazione || '',
          _uid: uid(),
        }));
        const abb = (abbRes.data || []).find((a) => a.attivo) || (abbRes.data || [])[0];
        // Indirizzo impianto: dal punto vendita del preventivo/fattura più recente, se diverso dalla sede
        let indirizzoImpianto = '';
        const docsConDest = (docRes.data || []).filter((d) => ['fattura', 'preventivo', 'offerta'].includes(d.tipo) && d.destinazione && !d.bozza);
        const docRif = docsConDest.find((d) => d.tipo === 'fattura') || docsConDest[0];
        if (docRif) {
          const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
          const sede = norm(cliRes.data.indirizzo_legale);
          if (!sede || !norm(docRif.destinazione).includes(sede)) indirizzoImpianto = docRif.destinazione;
        }
        const oggi = new Date();
        const traUnAnno = new Date(oggi); traUnAnno.setFullYear(oggi.getFullYear() + 1);
        const iso = (d) => d.toISOString().slice(0, 10);
        setForm((f) => ({
          ...f,
          apparecchiature,
          data_inizio: abb?.data_inizio || iso(oggi),
          data_fine: abb?.data_scadenza || iso(traUnAnno),
          canone_annuo: abb?.importo || 0,
          iban_fornitore: setRes.data?.iban || IBAN_DEFAULT,
          banca_fornitore: setRes.data?.banca || BANCA_DEFAULT,
          indirizzo_impianto: indirizzoImpianto,
        }));
        if (abb) toast.info('📅 Date e canone pre-compilati dall\'abbonamento attivo');
        if (indirizzoImpianto) toast.info('📍 Indirizzo impianto pre-compilato dal punto vendita del documento più recente');
      } catch (e) { console.error('Prefill contratto fallito:', e); }
    };
    init();
  }, [id, params]);

  const salva = async () => {
    if (!form.cliente_id) { toast.error('Cliente mancante'); return null; }
    setSaving(true);
    try {
      const payload = { ...form, canone_annuo: parseFloat(form.canone_annuo) || 0, durata_minima_anni: parseInt(form.durata_minima_anni, 10) || null };
      let cid = savedId;
      if (cid) {
        await axios.put(`${API}/contratti/${cid}`, payload);
      } else {
        const { data } = await axios.post(`${API}/contratti`, payload);
        cid = data.id;
        setSavedId(cid);
        navigate(`/contratti/${cid}`, { replace: true });
      }
      toast.success('✅ Contratto salvato');
      return cid;
    } catch (e) { toast.error(parseApiError(e, 'Errore salvataggio')); return null; }
    finally { setSaving(false); }
  };

  const attivaContratto = async () => {
    const cid = await salva();
    if (!cid) return;
    if (!window.confirm('Attivare il contratto?\n\nIl contratto passa ad ATTIVO (firma su carta): rinnovo tacito e fatturazione automatica partiranno alla scadenza.')) return;
    try {
      const { data } = await axios.post(`${API}/contratti/${cid}/attiva`);
      toast.success(`✅ ${data.message} — scadenza ${data.data_scadenza}, €${data.importo}`);
      setForm((f) => ({ ...f, stato: 'firmato', data_attivazione: new Date().toISOString(), data_inizio: data.data_inizio, data_fine: data.data_scadenza }));
    } catch (e) { toast.error(parseApiError(e, 'Errore attivazione contratto')); }
  };

  const isAttivo = form.stato === 'firmato' || !!form.abbonamento_id || !!form.data_attivazione;

  const eliminaContratto = async () => {
    if (!window.confirm('Eliminare definitivamente questa bozza di contratto?\n\nL\'operazione non è reversibile.')) return;
    try {
      await axios.delete(`${API}/contratti/${savedId}`);
      toast.success('🗑️ Contratto eliminato');
      navigate('/contratti');
    } catch (e) { toast.error(parseApiError(e, 'Errore eliminazione contratto')); }
  };

  const chiudiContratto = async () => {
    const avviso = avvisoDurataMinima(form);
    if (!window.confirm(`Chiudere il contratto?${avviso ? `\n\n${avviso}` : ''}\n\nNessuna fattura verrà più generata automaticamente.${avviso ? ' La decisione finale resta all\'operatore.' : ''}`)) return;
    try {
      await axios.post(`${API}/contratti/${savedId}/chiudi`, {});
      toast.success('Contratto chiuso: rinnovi e fatturazione automatica bloccati');
      setForm((f) => ({ ...f, disdetta: true }));
    } catch (e) { toast.error(parseApiError(e, 'Errore chiusura contratto')); }
  };

  const scaricaPdf = async (blank = false, stampa = false) => {
    const cid = await salva();
    if (!cid) return;
    try {
      const res = await axios.get(`${API}/contratti/${cid}/pdf?blank=${blank}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (stampa) { const w = window.open(url); if (w) w.onload = () => w.print(); }
      else {
        const a = document.createElement('a');
        a.href = url; a.download = `contratto${blank ? '_bianco' : ''}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
    } catch (e) { toast.error(parseApiError(e, 'Errore PDF')); }
  };

  const inviaEmail = async () => {
    const cid = await salva();
    if (!cid) return;
    setSendingEmail(true);
    try {
      const { data } = await axios.post(`${API}/contratti/${cid}/invia-email`, { email_extra: emailExtra || null });
      toast.success(`📧 Contratto inviato a: ${data.destinatari.join(', ')}`);
      setEmailDialogOpen(false);
      setEmailExtra('');
    } catch (e) { toast.error(parseApiError(e, 'Errore invio email'), { duration: 8000 }); }
    finally { setSendingEmail(false); }
  };

  const setApp = (i, k, v) => setForm((f) => {
    const a = [...f.apparecchiature]; a[i] = { ...a[i], [k]: v };
    const tot = a.reduce((s, x) => s + (parseFloat(x.canone) || 0), 0);
    return { ...f, apparecchiature: a, canone_annuo: tot > 0 ? Math.round(tot * 100) / 100 : f.canone_annuo };
  });
  const rimuoviApp = (i) => setForm((f) => {
    const a = f.apparecchiature.filter((_, j) => j !== i);
    const tot = a.reduce((s, x) => s + (parseFloat(x.canone) || 0), 0);
    return { ...f, apparecchiature: a, canone_annuo: tot > 0 ? Math.round(tot * 100) / 100 : f.canone_annuo };
  });
  const totCanoniApp = form.apparecchiature.reduce((s, x) => s + (parseFloat(x.canone) || 0), 0);

  const impiantiDisponibili = impiantiCliente.filter((imp) => {
    const m = (imp.matricola || '').trim().toLowerCase();
    return !m || !form.apparecchiature.some((a) => (a.matricola || '').trim().toLowerCase() === m);
  });

  const aggiungiDaImpianto = (imp) => {
    setForm((f) => ({
      ...f,
      apparecchiature: [...f.apparecchiature, {
        _uid: uid(),
        tipo: imp.tipo || '',
        marca_modello: `${imp.marca || ''} ${imp.modello || ''}`.trim(),
        matricola: imp.matricola || '',
        data_installazione: imp.data_installazione || '',
        data_scadenza: '',
        canone: '',
      }],
    }));
    setPickerImpiantiOpen(false);
    toast.success(`${imp.tipo || 'Impianto'} aggiunto: completa scadenza e canone`);
  };

  const Sez = ({ t }) => <h2 className="text-sm font-mono uppercase tracking-wider text-cyan-600 dark:text-cyan-400 mt-6 mb-2">{t}</h2>;

  return (
    <div className="max-w-4xl mx-auto pb-24 px-4">
      <div className="flex items-center gap-3 py-4">
        <Button variant="ghost" size="icon" className="rounded-none" onClick={() => navigate(-1)} data-testid="contratto-back"><ArrowLeft className="h-5 w-5" /></Button>
        <div>
          <h1 className="text-xl font-black uppercase flex items-center gap-2"><FileSignature className="h-5 w-5 text-cyan-500" /> Contratto di Assistenza Tecnica</h1>
          <p className="text-xs text-muted-foreground">{clienteNome || 'Cliente'} {savedId ? '· salvato' : '· nuovo'}</p>
        </div>
      </div>

      <Sez t="Durata e canone" />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div><Label className="text-xs font-mono uppercase">Decorrenza dal</Label>
          <Input type="date" value={form.data_inizio || ''} onChange={(e) => set('data_inizio', e.target.value)} className="rounded-none" data-testid="ctr-data-inizio" /></div>
        <div><Label className="text-xs font-mono uppercase">Al</Label>
          <Input type="date" value={form.data_fine || ''} onChange={(e) => set('data_fine', e.target.value)} className="rounded-none" data-testid="ctr-data-fine" /></div>
        <div><Label className="text-xs font-mono uppercase">Canone annuo € (+IVA)</Label>
          <Input type="number" step="0.01" value={form.canone_annuo} onChange={(e) => set('canone_annuo', e.target.value)} disabled={totCanoniApp > 0} className="rounded-none" data-testid="ctr-canone" />
          {totCanoniApp > 0 && <p className="text-[10px] text-emerald-600 mt-0.5" data-testid="ctr-canone-auto-hint">Calcolato automaticamente: somma dei canoni delle apparecchiature (€ {totCanoniApp.toFixed(2)})</p>}</div>
        <div><Label className="text-xs font-mono uppercase">Inizio fatturazione (omaggio)</Label>
          <Input type="date" value={form.data_inizio_fatturazione || ''} onChange={(e) => set('data_inizio_fatturazione', e.target.value)} className="rounded-none" data-testid="ctr-inizio-fatturazione" />
          <p className="text-[10px] text-muted-foreground mt-0.5">Vuoto = fatturazione da subito. Data futura = periodo omaggio: nessuna fattura né scadenza fino ad allora.</p></div>
        <div><Label className="text-xs font-mono uppercase">Durata minima contratto (anni)</Label>
          <Input type="number" min="1" step="1" placeholder="es. 3" value={form.durata_minima_anni || ''} data-testid="ctr-durata-minima"
            onChange={(e) => {
              const v = e.target.value;
              setForm((f) => ({ ...f, durata_minima_anni: v, penale_uscita: v ? (f.penale_uscita || 'canoni residui al momento della disdetta') : '' }));
            }}
            className="rounded-none" />
          <p className="text-[10px] text-muted-foreground mt-0.5">Vuoto = nessuna durata minima. Se impostata, nel contratto stampato appare la clausola di durata minima (Art. 5).</p></div>
        {form.durata_minima_anni && (
          <div><Label className="text-xs font-mono uppercase">Penale uscita anticipata</Label>
            <Input value={form.penale_uscita || ''} onChange={(e) => set('penale_uscita', e.target.value)}
              placeholder="canoni residui al momento della disdetta" className="rounded-none" data-testid="ctr-penale-uscita" />
            <p className="text-[10px] text-muted-foreground mt-0.5">Modificabile: importo fisso, percentuale o testo libero. Stampata in Art. 5.</p></div>
        )}
      </div>

      {savedId && (
        <>
          <Sez t={`Storico pagamenti (${storicoPagamenti.length})`} />
          {storicoPagamenti.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="storico-pagamenti-vuoto">Nessun pagamento registrato per questo contratto. Segna il canone come PAGATO dalla pagina Contratti.</p>
          ) : (
            <div className="border border-border" data-testid="storico-pagamenti">
              <table className="data-table">
                <thead>
                  <tr><th>Periodo</th><th>Importo</th><th>Pagato il</th><th>Registrato da</th></tr>
                </thead>
                <tbody>
                  {[...storicoPagamenti].reverse().map((s, i) => (
                    <tr key={i} data-testid={`storico-pagamento-riga-${i}`}>
                      <td className="font-mono text-xs">{fmtIt(s.periodo_inizio)} → {fmtIt(s.periodo_fine)}</td>
                      <td className="font-mono text-xs">{(s.importo || 0).toFixed(2)} € + IVA</td>
                      <td className="font-mono text-xs text-emerald-600 font-bold">{fmtIt(s.pagato_il)}</td>
                      <td className="text-xs text-muted-foreground">{s.registrato_da || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Sez t="Modalità di pagamento" />
      <div className="flex flex-wrap gap-1">
        {['Bonifico anticipato', 'Contanti', 'RiBa'].map((m) => (
          <Button key={m} type="button" variant={form.modalita_pagamento === m ? 'default' : 'outline'} className="rounded-none" onClick={() => set('modalita_pagamento', m)} data-testid={`ctr-pag-${m}`}>{m}</Button>
        ))}
      </div>
      {form.modalita_pagamento === 'RiBa' && (
        <div className="border border-border p-3 mt-3 space-y-2 bg-muted/30">
          <p className="text-xs font-mono uppercase text-muted-foreground">Dati bancari del cliente (RiBa)</p>
          <div className="grid md:grid-cols-2 gap-2">
            <div><Label className="text-xs font-mono uppercase">Banca</Label>
              <Input value={form.riba_banca || ''} onChange={(e) => set('riba_banca', e.target.value)} className="rounded-none" data-testid="ctr-riba-banca" /></div>
            <div><Label className="text-xs font-mono uppercase">Agenzia / Filiale</Label>
              <Input value={form.riba_agenzia || ''} onChange={(e) => set('riba_agenzia', e.target.value)} className="rounded-none" data-testid="ctr-riba-agenzia" /></div>
          </div>
          <div><Label className="text-xs font-mono uppercase">IBAN Cliente</Label>
            <Input value={form.riba_iban || ''} onChange={(e) => set('riba_iban', e.target.value)} className="rounded-none font-mono" data-testid="ctr-riba-iban" /></div>
          <div><Label className="text-xs font-mono uppercase">Scadenze</Label>
            <div className="flex gap-1 mt-1">
              {['Unica soluzione', 'Trimestrali', 'Semestrali'].map((s) => (
                <Button key={s} type="button" size="sm" variant={form.riba_scadenze === s ? 'default' : 'outline'} className="rounded-none text-xs" onClick={() => set('riba_scadenze', s)}>{s}</Button>
              ))}
            </div></div>
        </div>
      )}

      <Sez t="Coordinate bancarie Velta Sistemi" />
      <div className="grid md:grid-cols-2 gap-2">
        <div><Label className="text-xs font-mono uppercase">IBAN</Label>
          <Input value={form.iban_fornitore || ''} onChange={(e) => set('iban_fornitore', e.target.value)} className="rounded-none font-mono" data-testid="ctr-iban-fornitore" /></div>
        <div><Label className="text-xs font-mono uppercase">Banca</Label>
          <Input value={form.banca_fornitore || ''} onChange={(e) => set('banca_fornitore', e.target.value)} className="rounded-none" data-testid="ctr-banca-fornitore" /></div>
      </div>

      <Sez t="Sede impianto / punto vendita" />
      <div>
        <Label className="text-xs font-mono uppercase">Indirizzo impianto/cassa (se diverso dalla sede principale)</Label>
        <Input value={form.indirizzo_impianto || ''} onChange={(e) => set('indirizzo_impianto', e.target.value)}
          placeholder="es. Punto vendita Gavardo - Via Roma 12, Gavardo (BS)" className="rounded-none" data-testid="ctr-indirizzo-impianto" />
        <p className="text-[11px] text-muted-foreground mt-1">Se compilato, nel contratto compaiono sia la sede principale sia la sede impianto. Viene pre-compilato dal punto vendita indicato nel preventivo/fattura più recente.</p>
      </div>

      <Sez t="Apparecchiature in assistenza" />
      {form.apparecchiature.length > 0 && (
        <div className="grid grid-cols-12 gap-1 text-[9px] font-mono uppercase text-muted-foreground px-0.5">
          <span className="col-span-2">Tipo</span><span className="col-span-2">Marca/Modello</span><span className="col-span-2">Matricola</span>
          <span className="col-span-2">Data attivazione</span><span className="col-span-2">Data scadenza</span><span className="col-span-1">Canone €</span><span />
        </div>
      )}
      {form.apparecchiature.map((a, i) => (
        <div key={a._uid || `app-${i}`} className="grid grid-cols-12 gap-1 mb-1 items-center">
          <Input placeholder="Tipo" value={a.tipo || ''} onChange={(e) => setApp(i, 'tipo', e.target.value)} className="rounded-none text-xs col-span-2" />
          <Input placeholder="Marca/Modello" value={a.marca_modello || ''} onChange={(e) => setApp(i, 'marca_modello', e.target.value)} className="rounded-none text-xs col-span-2" />
          <Input placeholder="Matricola" value={a.matricola || ''} onChange={(e) => setApp(i, 'matricola', e.target.value)} className="rounded-none text-xs font-mono col-span-2" />
          <Input type="date" title="Data attivazione" value={a.data_installazione || ''} onChange={(e) => setApp(i, 'data_installazione', e.target.value)} className="rounded-none text-xs col-span-2" data-testid={`ctr-app-attivazione-${i}`} />
          <Input type="date" title="Data scadenza (fattura automatica alla scadenza)" value={a.data_scadenza || ''} onChange={(e) => setApp(i, 'data_scadenza', e.target.value)} className="rounded-none text-xs col-span-2" data-testid={`ctr-app-scadenza-${i}`} />
          <Input type="number" step="0.01" min="0" placeholder="€" value={a.canone ?? ''} onChange={(e) => setApp(i, 'canone', e.target.value)} className="rounded-none text-xs col-span-1" data-testid={`ctr-app-canone-${i}`} />
          <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => rimuoviApp(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={() => set('apparecchiature', [...form.apparecchiature, { _uid: uid() }])} data-testid="ctr-add-app"><Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi manuale</Button>
        <Popover open={pickerImpiantiOpen} onOpenChange={setPickerImpiantiOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="rounded-none border-cyan-500 text-cyan-700 dark:text-cyan-400" data-testid="ctr-add-da-impianti"><HardDrive className="h-3.5 w-3.5 mr-1" /> Aggiungi da impianti CRM</Button>
          </PopoverTrigger>
          <PopoverContent className="rounded-none p-0 w-96" align="start">
            <p className="text-[10px] font-mono uppercase text-muted-foreground p-2 border-b border-border">Impianti registrati del cliente (clic per aggiungere)</p>
            <div className="max-h-60 overflow-y-auto divide-y divide-border">
              {impiantiDisponibili.length === 0 ? (
                <p className="text-xs text-muted-foreground p-3">Nessun impianto disponibile (già tutti aggiunti o nessuno registrato)</p>
              ) : impiantiDisponibili.map((imp) => (
                <button key={imp.id} type="button" className="w-full text-left p-2 hover:bg-accent text-xs flex items-center justify-between gap-2"
                  onClick={() => aggiungiDaImpianto(imp)} data-testid={`ctr-picker-impianto-${imp.id}`}>
                  <span><span className="font-bold">{imp.tipo}</span> {`${imp.marca || ''} ${imp.modello || ''}`.trim()}</span>
                  <span className="font-mono text-muted-foreground">{imp.matricola || '-'}</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="grid md:grid-cols-2 gap-2 mt-4">
        <div><Label className="text-xs font-mono uppercase">Luogo firma</Label>
          <Input value={form.luogo_firma || ''} onChange={(e) => set('luogo_firma', e.target.value)} placeholder="Manerba del Garda" className="rounded-none" data-testid="ctr-luogo" /></div>
        <div><Label className="text-xs font-mono uppercase">Note</Label>
          <Textarea value={form.note || ''} onChange={(e) => set('note', e.target.value)} className="rounded-none min-h-9" data-testid="ctr-note" /></div>
      </div>

      <div className={`border p-3 mt-4 flex items-start gap-3 ${form.disdetta ? 'border-destructive bg-destructive/10' : 'border-border bg-muted/30'}`}>
        <input type="checkbox" id="ctr-disdetta" checked={!!form.disdetta}
          onChange={(e) => {
            if (e.target.checked) {
              const avviso = avvisoDurataMinima(form);
              if (avviso && !window.confirm(`${avviso}\n\nConfermi comunque la disdetta? La decisione finale resta all'operatore.`)) return;
            }
            set('disdetta', e.target.checked);
          }}
          className="mt-1 h-4 w-4 accent-red-600" data-testid="ctr-disdetta" />
        <label htmlFor="ctr-disdetta" className="text-sm cursor-pointer">
          <span className="font-semibold">Disdetta ricevuta — NON rinnovare</span>
          <p className="text-xs text-muted-foreground mt-0.5">Se spuntato, alla scadenza il contratto NON verrà rinnovato automaticamente. Altrimenti si rinnova da solo di 12 mesi (rinnovo tacito) con notifica email a te.</p>
        </label>
      </div>

      <Sez t="Firma del cliente — Contratto" />
      <div className="max-w-xs mb-2"><Label className="text-xs font-mono uppercase">Nome e cognome firmatario</Label>
        <Input value={form.firma_cliente_nome || ''} onChange={(e) => set('firma_cliente_nome', e.target.value)} className="rounded-none" data-testid="ctr-firma-nome" /></div>
      <SignaturePad value={form.firma_cliente_b64} onChange={(d) => set('firma_cliente_b64', d)} />

      <Sez t="Firma del cliente — Clausole vessatorie (artt. 1341-1342 c.c.)" />
      <p className="text-xs text-muted-foreground mb-2">Approvazione specifica di: Art. 3, 5, 6, 8, 10, 12.</p>
      <SignaturePad value={form.firma_clausole_b64} onChange={(d) => set('firma_clausole_b64', d)} />

      {/* Barra azioni fissa */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border p-3 flex flex-wrap items-center gap-2 z-40">
        {savedId && !form.disdetta && (
          <div className="mr-auto">
            {isAttivo ? (
              <Button variant="outline" onClick={chiudiContratto} className="rounded-none text-red-600 border-red-500 hover:bg-red-50 dark:hover:bg-red-950/30" data-testid="ctr-chiudi-btn">
                <XCircle className="h-4 w-4 mr-2" />Chiudi contratto
              </Button>
            ) : (
              <Button variant="outline" onClick={eliminaContratto} className="rounded-none text-red-600 border-red-500 hover:bg-red-50 dark:hover:bg-red-950/30" data-testid="ctr-elimina-btn">
                <Trash2 className="h-4 w-4 mr-2" />Elimina contratto
              </Button>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-2 justify-center flex-1">
        <Button onClick={salva} disabled={saving} className="rounded-none" data-testid="ctr-salva-btn"><Save className="h-4 w-4 mr-2" />{saving ? 'Salvo...' : 'Salva'}</Button>
        {!(form.abbonamento_id || form.data_attivazione) ? (
          <Button variant="outline" onClick={attivaContratto} className="rounded-none text-emerald-600 border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-950/30" data-testid="ctr-attiva-btn">
            <CheckCircle2 className="h-4 w-4 mr-2" />Attiva contratto (firmato su carta)
          </Button>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-emerald-600 border border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" data-testid="ctr-attivo-badge">
            <CheckCircle2 className="h-4 w-4" /> Contratto attivo
          </span>
        )}
        <Button variant="outline" onClick={() => scaricaPdf(false)} className="rounded-none" data-testid="ctr-pdf-btn"><FileDown className="h-4 w-4 mr-2" />PDF</Button>
        <Button variant="outline" onClick={() => scaricaPdf(true)} className="rounded-none" data-testid="ctr-pdf-blank-btn"><Printer className="h-4 w-4 mr-2" />Stampa in bianco</Button>
        <Button variant="outline" onClick={() => setEmailDialogOpen(true)} className="rounded-none" data-testid="ctr-email-btn"><Mail className="h-4 w-4 mr-2" />Invia al cliente</Button>
        </div>
      </div>

      {/* Dialog invio email */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-none">
          <DialogHeader><DialogTitle className="font-mono uppercase">Invia contratto via email</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Il contratto verrà inviato all'email salvata nel CRM. Puoi aggiungere un'altra email indicata dal cliente al momento.</p>
          <div><Label className="text-xs font-mono uppercase">Email aggiuntiva (opzionale)</Label>
            <Input type="email" value={emailExtra} onChange={(e) => setEmailExtra(e.target.value)} placeholder="es. mario.rossi@gmail.com" className="rounded-none" data-testid="ctr-email-extra" /></div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setEmailDialogOpen(false)}>Annulla</Button>
            <Button className="rounded-none" onClick={inviaEmail} disabled={sendingEmail} data-testid="ctr-email-send-btn">
              <Mail className="h-4 w-4 mr-2" />{sendingEmail ? 'Invio...' : 'Invia'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
